import type { FC } from "react";

import type { RepositoryIndexingRun } from "../../types";

import { Button } from "../common/Button";
import { DataState } from "../common/DataState";
import { TableSkeleton } from "../common/LoadingState";
import { PanelHeaderBlock } from "../common/PanelHeaderBlock";
import { PanelSection } from "../common/PanelSection";
import { IndexingRunCard } from "./IndexingRunCard";

interface Props {
  runs: RepositoryIndexingRun[];
  loading: boolean;
  error: string | null;
  selectedRunId: string | null;
  onRefresh: () => void;
  onSelectRun: (runId: string) => void;
}

export const RepositoryIndexingRunsPanel: FC<Props> = ({
  runs,
  loading,
  error,
  selectedRunId,
  onRefresh,
  onSelectRun,
}) => {
  const isLoadingInitial = loading && runs.length === 0;

  return (
    <PanelSection
      headerContent={
        <PanelHeaderBlock
          eyebrow="Step 1"
          title="Select Run"
          description="Choose the run that should drive the insights pane."
        />
      }
    >
      <DataState
        loading={isLoadingInitial}
        error={error}
        errorAction={
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            Try refreshing again
          </Button>
        }
        empty={runs.length === 0 && !loading}
        emptyTitle="No runs recorded"
        emptyDescription="Repository indexing runs will appear here once triggered."
        loadingComponent={
          <div className="rounded-none border border-slate-300 p-3 dark:border-slate-700">
            <TableSkeleton rows={3} columns={1} />
          </div>
        }
      >
        <div className="custom-scrollbar max-h-[28rem] overflow-hidden overflow-y-auto rounded-none border border-slate-300 dark:border-slate-700">
          <ul className="divide-y divide-gray-200 dark:divide-white/10">
            {runs.map((run) => (
              <IndexingRunCard
                key={run.run_id}
                run={run}
                selected={run.run_id === selectedRunId}
                onSelect={onSelectRun}
              />
            ))}
          </ul>
        </div>
      </DataState>
    </PanelSection>
  );
};
